import React, { useContext, useEffect, useState } from "react";
import Grid from '@mui/material/Grid2';
import { Typography, Stack } from "@mui/material";
import { Device } from "@services/devices/types";
import { getDevice } from "@services/devices";
import DeviceHeader from "../../DeviceHeader";
import Charts from "./Charts";
import StatisticsList from "./StatisticsList";
import { useTranslation } from "react-i18next";
import { usePolling } from "@common/hooks";
import { CardHolder } from "..";
import CardFooter from "../footer";
import { ConferenceParticipant } from "@services/meetings/types";
import {
  inThePast,
  isParticipantConnected,
  isParticipantManaged,
} from "@common/utils/meetings";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";

export const CardContent = ({
  device,
  participant,
  hideHeader = false,
  hideFooter = false,
}: {
  device?: Device;
  hideHeader?: boolean;
  hideFooter?: boolean;
  participant: ConferenceParticipant;
}) => {
  const { t } = useTranslation();
  const context = useContext(RoomsRealtimeContext);
  const meeting = { ...context.detail?.meeting };
  const displayContent = () => {
    if (!isParticipantManaged(participant)) {
      return (
        <Typography>{t("This device is not managed by Symphony")}</Typography>
      );
    } else if (isParticipantConnected(participant) || inThePast(meeting.end)) {
      return (
        <Stack
          sx={{
            width: "100%",
            gap: 2,
          }}
        >
          <Charts device={device} />
          <StatisticsList device={device} />
        </Stack>
      );
    }
    return <Typography>{t("The device is not in this call")}</Typography>;
  };
  return (
    <>
      {!hideHeader && <DeviceHeader device={device} fullWidth />}
      <Grid
        sx={(theme) => ({
          marginRight: theme.spacing(1),
          marginLeft: theme.spacing(1),
          display: "flex",
          flex: 1,
        })}
      >
        {displayContent()}
      </Grid>
      {!hideFooter && <CardFooter device={device} />}
    </>
  );
};

const Card = ({ participant }: { participant: ConferenceParticipant }) => {
  const [device, setDevice] = useState<Device>();

  const deviceId = participant?.device?.id;

  const fetch = async (deviceId: Device["id"]) => {
    if (deviceId !== undefined) {
      const device = await getDevice(deviceId);
      setDevice(device);
      if (!isParticipantManaged(participant)) poll.stop();
    }
  };

  const poll = usePolling(() => {
    fetch(deviceId);
  }, 6e4);
  useEffect(() => {
    if (poll.isPolling) poll.stop();
    poll.start();
  }, [deviceId]);
  return (
    <CardHolder device={device}>
      <CardContent device={device} participant={participant} />
    </CardHolder>
  );
};

export default Card;
